import { Box, Grid, Popover, Stack, Typography, useTheme } from "@mui/material";
import { AnimatedCheckMark, AnimatedX, ProgressButton } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";

import { useRef, useState } from "react";

import { useAppSelector } from "~/app/store";

import envConstants from "~/config/envConstants";
import useFlag from "~/config/flags";
import { selectCurrentBatchId } from "~/features/autostorePicking/autostorePicking.slice";
import { selectPtlSimulationEnabled } from "~/features/devTools/devTools.slice";
import { usePortStatusQuery } from "~/hooks/usePortStatus";
import { ternaryIff } from "~/lib/helpers";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { usePickSubscription } from "~/lib/signalr";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import {
  selectCartNumberConfirmed,
  selectIsPickQuantityConfirmed,
  selectNextPickingStateLoading,
  selectSignalR
} from "~/redux/selectors/autostoreSelectors";
import { selectAbleQtyToPick } from "~/redux/selectors/outOfStockSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import {
  selectWorkstationAvailablePorts,
  selectWorkstationId
} from "~/redux/selectors/workstationsSelectors";
import {
  useGetFocusedPickQuery,
  useGetTotesForBatchQuery
} from "~/redux/warehouse/autostorePicking.hooks";
import { SignalRPickEventDto } from "~/types/api";

import { AutostoreToteHeader } from "./AutostoreToteHeader";
import ExpressIcon from "./ExpressIcon";
import PTL from "./PTL";
import ToteProgressBarV2 from "./ToteProgressBarV2";

function AutostoreTote(props: {
  toteId: Guid;
  position: number;
  externalToteId?: Guid;
  isActiveTote: boolean;
  onClickCb: () => Promise<void | null> | null;
  isLastStackedTote?: boolean;
}) {
  const { palette } = useTheme();
  const {
    toteId,
    position,
    externalToteId,
    isActiveTote,
    onClickCb,
    isLastStackedTote
  } = props;

  const lastRefetchOccurredOn = useRef<number | null>(null);
  const ableToPickQty = useAppSelector(selectAbleQtyToPick);
  const nextPickingStateLoading = useAppSelector(selectNextPickingStateLoading);
  const cartNumberConfirmed = useAppSelector(selectCartNumberConfirmed);
  const fulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const isPickQuantityConfirmed = useAppSelector(selectIsPickQuantityConfirmed);
  const ptlSimulationEnabled = useAppSelector(selectPtlSimulationEnabled);
  const workstationId = useAppSelector(selectWorkstationId);
  const signalR = useAppSelector(selectSignalR);
  const [portId] = useAppSelector(selectWorkstationAvailablePorts);
  const { enableColorChanges } = useFlag();
  const currentBatchId = useAppSelector(selectCurrentBatchId);

  const [externalToteIdPopover, setExternalToteIdPopover] =
    useState<HTMLElement | null>(null);

  const portStatus = usePortStatusQuery(portId);
  const { isReady, isBinOpen } = portStatus;
  const isBinReadyForScan = isReady || isBinOpen;

  const { data: focusedPick, refetch: refetchFocusedPick } =
    useGetFocusedPickQuery(
      workstationId ? { workstationId: workstationId } : skipToken,
      {
        refetchOnMountOrArgChange: true,
        pollingInterval:
          signalR?.state?.toLowerCase() === "disconnected" ? 2000 : undefined
      }
    );
  const batchId = focusedPick?.batchId ?? currentBatchId;

  const { tote } = useGetTotesForBatchQuery(
    batchId && position ? { batchId } : skipToken,
    {
      pollingInterval:
        signalR?.state?.toLowerCase() === "disconnected" ? 2000 : undefined,
      refetchOnMountOrArgChange: 5,
      selectFromResult: ({ data }) => ({
        tote: data
          ?.flatMap((pos) => pos.totes)
          ?.find((tote) => tote.toteId === toteId)
      })
    }
  );

  const isExpressOrder = tote?.priority === "Express" || false;

  const {
    ap_confirmCartNumberEnabled,
    ap_fusionPortScreenEnabled,
    ap_isTopAreaOfToteClickable
  } = useAppSelector(selectServerConfig);

  const fusionPortScreen = ap_fusionPortScreenEnabled;
  const isTopAreaClickable = ap_isTopAreaOfToteClickable;

  const pickReady =
    isActiveTote &&
    !!isBinReadyForScan &&
    !nextPickingStateLoading &&
    (!ap_confirmCartNumberEnabled || cartNumberConfirmed);

  // state that should indicate that pick quantity is confirmed
  const confirmDisabled =
    (!!fulfillmentCenter &&
      fulfillmentCenter.pickQuantityConfirmationEnabled &&
      !isPickQuantityConfirmed) ||
    !isBinReadyForScan;

  const isToteConfirmDisabled = !!(confirmDisabled || !pickReady);

  const refetchFocusedPickAndToteContent = () => {
    try {
      const currentDate = Date.now();
      if (lastRefetchOccurredOn.current) {
        const refetchCalledBeforeSeconds =
          currentDate - lastRefetchOccurredOn.current;
        lastRefetchOccurredOn.current = currentDate;
        if (refetchCalledBeforeSeconds < 1000) return;
      }
      lastRefetchOccurredOn.current = currentDate;
      void refetchFocusedPick();
    } catch (e) {
      getMessageFromRtkError(e);
    }
  };

  // Show Checkmark icon on the tote box if:
  // - All picks in the tote are in the 'Completed' status
  // - There is at least one completed pick in the tote and tote is in the 'Picked' status
  const getShowCheckmark = () => {
    if (tote && tote.picks) {
      const isEveryPickCompleted = tote.picks.every(
        (p) => !!p.completedTimestamp
      );
      if (isEveryPickCompleted) return true;

      const isSomePickCompleted = tote.picks.some(
        (p) => !!p.completedTimestamp
      );

      return isSomePickCompleted && tote.pickedTimestamp;
    }
    return false;
  };

  // Show X icon on the tote box if:
  // - If the tote is in the 'Canceled' status
  // - All picks in the tote are in the 'Canceled' status
  const getShowX = () => {
    if (!tote) return false;
    if (tote.canceledTimestamp) return true;
    const isEveryPickCanceled = tote.picks?.every((p) => !!p.canceledTimestamp);
    return !!isEveryPickCanceled;
  };

  const showCheckmark = getShowCheckmark();
  const showX = getShowX();

  const pickSubscription = (event: SignalRPickEventDto) => {
    if (event.batchId === batchId && event.pick.assignedToteId === toteId) {
      refetchFocusedPickAndToteContent();
    }
  };
  usePickSubscription(pickSubscription);

  let toteStyle: React.CSSProperties = {
    backgroundColor: isActiveTote
      ? ternaryIff(
          isToteConfirmDisabled,
          enableColorChanges
            ? palette.autostoreDesignSystem.surface.interactive.accent.disabled
            : palette.gray.contrastText,
          enableColorChanges
            ? palette.autostoreDesignSystem.surface.interactive.accent.default
            : palette.primary.main
        )
      : enableColorChanges
        ? palette.autostoreDesignSystem.surface.interactive.muted.default
        : palette.secondary.main,
    color: isActiveTote
      ? enableColorChanges
        ? palette.autostoreDesignSystem.text.onInverse.primary
        : palette.primary.contrastText
      : enableColorChanges
        ? palette.autostoreDesignSystem.text.onStandard.secondary
        : palette.darkGray.light
  };
  if (showCheckmark)
    toteStyle = {
      backgroundColor: enableColorChanges
        ? palette.autostoreDesignSystem.surface.static.positive
        : palette.success.light,
      color: enableColorChanges
        ? palette.autostoreDesignSystem.icon.onStandard.statusPositive
        : palette.success.main
    };

  const matchingPickQuantity =
    focusedPick && (ableToPickQty || focusedPick.quantity.value);

  const showPickQuantity = isActiveTote;

  const checkColor = pickReady
    ? enableColorChanges
      ? palette.autostoreDesignSystem.icon.onStandard.statusPositive
      : palette.success.main
    : "#0000001f";

  const progressBar =
    tote?.picks && isLastStackedTote ? (
      <ToteProgressBarV2
        allPicksForTote={tote.picks}
        hasExternalToteId={!!externalToteId}
      />
    ) : null;

  return (
    <Stack
      style={{
        textAlign: "center",
        padding: "0 4px",
        maxWidth: 240,
        margin: "0 auto",
        gap: 8
      }}
      aria-label="Autostore Tote"
    >
      <Stack direction="row">
        <Box flex={1}>
          <AutostoreToteHeader
            tote={tote}
            isActiveTote={isActiveTote}
            toteId={toteId}
            pickReady={pickReady}
            position={position}
            isLastStackedTote={isLastStackedTote}
            confirmDisabled={confirmDisabled}
          />
          <Box
            sx={{
              position: "relative",
              ...(enableColorChanges && {
                "& > span": { display: "block", width: "100%" }
              })
            }}
          >
            {!isTopAreaClickable && (
              <Box
                sx={{
                  height: "35px",
                  width: "100%",
                  position: "absolute",
                  top: "-7%",
                  zIndex: 1
                }}
              />
            )}
            <ProgressButton
              buttonSize="xLarge"
              emphasis="high"
              responsive
              variant="contained"
              aria-label="active tote"
              disabled={isToteConfirmDisabled}
              onClick={async () => {
                try {
                  await onClickCb();
                  refetchFocusedPickAndToteContent();
                } catch (e) {
                  getMessageFromRtkError(e);
                }
              }}
              style={toteStyle}
              sx={{
                "@keyframes pulse": {
                  from: {
                    boxShadow: "0 0 0 0px rgba(0, 0, 0, 0.4)"
                  },
                  to: {
                    boxShadow: "0 0 0 20px rgba(0, 0, 0, 0)"
                  }
                },
                width: "100%",
                ...(pickReady &&
                  !isToteConfirmDisabled && {
                    animation: "pulse 2s infinite ease"
                  })
              }}
            >
              <Box
                height={
                  (envConstants.ENABLE_PTL_SIMULATION === "true" &&
                    ptlSimulationEnabled) ||
                  fusionPortScreen
                    ? 140
                    : 240
                }
                width="100%"
              >
                <Grid
                  container
                  spacing={2}
                  direction="row"
                  wrap="nowrap"
                  style={{
                    height: "100%",
                    position: "relative"
                  }}
                >
                  <Grid item xs={4}>
                    {isLastStackedTote && (
                      <Box textAlign="left">
                        <Typography
                          variant="h3"
                          aria-label="tote position"
                        >{`P${tote?.totePosition || ""}`}</Typography>
                      </Box>
                    )}
                  </Grid>
                  {(showCheckmark || showX) &&
                    isExpressOrder &&
                    isLastStackedTote && (
                      <Grid
                        item
                        sx={{ position: "absolute", top: 0, right: -5 }}
                      >
                        <ExpressIcon
                          expressIconSizePx={25}
                          translateVerticallyExpressIconPercentage={10}
                          translateVerticallyExpressTextPercentage={-55}
                          expressTextVariant="caption"
                          expressTextWeight={700}
                        />
                      </Grid>
                    )}
                  <Grid
                    item
                    xs={4}
                    container
                    justifyContent="center"
                    style={{
                      marginTop: fusionPortScreen ? "0.75em" : "1.25em"
                    }}
                  >
                    {showCheckmark && isLastStackedTote && (
                      <AnimatedCheckMark checkColor={checkColor} size={60} />
                    )}
                    {showX && isLastStackedTote && (
                      <AnimatedX color={palette.autostoreRed.main} size={60} />
                    )}
                    {!(showCheckmark || showX) &&
                      isExpressOrder &&
                      isLastStackedTote && (
                        <ExpressIcon
                          expressIconSizePx={50}
                          translateVerticallyExpressIconPercentage={10}
                          translateVerticallyExpressTextPercentage={-45}
                          expressTextVariant="body2"
                          expressTextWeight={700}
                        />
                      )}
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    container
                    alignItems="flex-end"
                    justifyContent="flex-end"
                    style={{
                      position: fusionPortScreen ? "absolute" : undefined,
                      right: fusionPortScreen ? 0 : undefined,
                      bottom: fusionPortScreen ? "-14%" : undefined
                    }}
                  >
                    {showPickQuantity && (
                      <Typography variant="h3" aria-label="pick quantity">
                        {matchingPickQuantity || ""}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </ProgressButton>
            {enableColorChanges && progressBar}
          </Box>

          {externalToteId && isLastStackedTote && (
            <Box textAlign="center" mt={1}>
              <Typography
                aria-label="external tote id"
                variant="h5"
                color={palette.toteText.primary}
                onClick={(event) =>
                  setExternalToteIdPopover(event.currentTarget)
                }
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  opacity: 0.6,
                  cursor: "pointer",
                  width: "100%",
                  maxWidth: "175px",
                  marginX: "auto"
                }}
              >
                {externalToteId}
              </Typography>
              <Popover
                open={!!externalToteIdPopover}
                anchorEl={externalToteIdPopover}
                onClose={() => setExternalToteIdPopover(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center"
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center"
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "1.2rem",
                    padding: "12px 16px",
                    bgcolor: "rgba(0, 0, 0, 0.9)",
                    color: "white"
                  }}
                >
                  {externalToteId}
                </Typography>
              </Popover>
            </Box>
          )}
        </Box>

        {!enableColorChanges && progressBar}
      </Stack>

      {envConstants.ENABLE_PTL_SIMULATION === "true" &&
        ptlSimulationEnabled &&
        isLastStackedTote && <PTL position={tote?.totePosition || 0} />}
    </Stack>
  );
}

export default AutostoreTote;
